import * as React from "react"
import * as ReactDOM from "react-dom"
import {ui_state} from "../state/index"
import type {Card, CardUID, Board} from "@cling/lib.shared.model"
import {Snackbar} from "@cling/lib.web.mdc"
import {AddCardActions, below_card_insert_pos} from "../card/add_card_actions"
import {report_error} from "@cling/lib.shared.debug"
import {BoardContext} from "../board_context"
import {observer} from "mobx-react"
import {CSSTransition} from "react-transition-group"
import {i18n} from "@cling/lib.web.i18n"
import {useCardPermission} from "../state/permission_hooks"
import {can_call_faas} from "@cling/lib.web.auth"
import {files_from_data_transfer} from "../upload/upload"
import {observable, runInAction, makeObservable} from "mobx"
import {cancel_event} from "@cling/lib.web.utils"

let client_x: number
let client_y: number

class GlobalDragAndPasteState {
    private _enabled = true

    constructor() {
        makeObservable<GlobalDragAndPasteState, "_enabled">(this, {
            _enabled: observable,
        })
    }

    enable() {
        runInAction(() => (this._enabled = true))
    }

    disable() {
        runInAction(() => (this._enabled = false))
    }

    get enabled() {
        return this._enabled
    }
}

export const global_drag_and_drop_state = new GlobalDragAndPasteState()

export const GlobalDropAndPasteHandler: React.FunctionComponent = observer(() => {
    const {
        current_board: {board, display_version},
    } = React.useContext(BoardContext)
    const dragging_timeout = React.useRef<any>()
    const [dragging, set_dragging] = React.useState(false)
    const dragging_on = React.useCallback((e?) => {
        if (!global_drag_and_drop_state.enabled) {
            return
        }
        clearTimeout(dragging_timeout.current)
        dragging_timeout.current = setTimeout(() => set_dragging(false), 3000)
        e?.preventDefault()
        e?.stopPropagation()
        set_dragging(true)
    }, [])
    const dragging_off = React.useCallback((e?) => {
        if (!global_drag_and_drop_state.enabled) {
            return
        }
        clearTimeout(dragging_timeout.current)
        e?.preventDefault()
        e?.stopPropagation()
        set_dragging(false)
    }, [])
    const permissions = useCardPermission(board?.root)
    React.useEffect(() => {
        if (
            display_version !== "latest" ||
            !board ||
            !permissions.can_add_card ||
            !global_drag_and_drop_state.enabled
        ) {
            return
        }
        const listener = paste_drop_listener(board)
        document.body.addEventListener("mousemove", record_mouse_position)
        document.addEventListener("paste", listener)
        document.body.addEventListener("drop", listener)
        document.body.addEventListener("drop", dragging_off)
        document.body.addEventListener("dragenter", dragging_on)
        document.body.addEventListener("dragend", dragging_off)
        return () => {
            document.body.removeEventListener("mousemove", record_mouse_position)
            document.removeEventListener("paste", listener)
            document.body.removeEventListener("drop", listener)
            document.body.removeEventListener("drop", dragging_off)
            document.body.removeEventListener("dragenter", dragging_on)
            document.body.removeEventListener("dragend", dragging_off)
        }
    }, [board, display_version, dragging_off, dragging_on, permissions])
    React.useEffect(() => {
        if (!dragging) {
            return
        }
        // Disable browser's default behavior.
        // See https://developer.mozilla.org/en-US/docs/Web/API/HTML_Drag_and_Drop_API/File_drag_and_drop#prevent_the_browsers_default_drag_behavior
        document.body.addEventListener("dragover", cancel_event)
        return () => {
            document.body.removeEventListener("dragover", cancel_event)
        }
    }, [dragging])
    if (!dragging || !global_drag_and_drop_state.enabled) {
        return null
    }
    return ReactDOM.createPortal(
        <CSSTransition
            classNames={{
                appearActive: "global-drop-and-paste--appear",
                appearDone: "global-drop-and-paste--appear",
            }}
            in
            appear
            timeout={10}
        >
            <div className="global-drop-and-paste" onDragLeave={dragging_off}>
                <div className="global-drop-and-paste__title">
                    <div>{i18n.place_files_on_your_board}</div>
                </div>
            </div>
        </CSSTransition>,
        document.body,
    )
})

const record_mouse_position = (e: MouseEvent) => {
    client_x = e.clientX
    client_y = e.clientY
}

const paste_drop_listener = (board: Board) => async (e: ClipboardEvent | DragEvent) => {
    if (!global_drag_and_drop_state.enabled) {
        return
    }
    try {
        if (!can_call_faas()) {
            Snackbar.show_message(i18n.upload_currently_not_possible)
            return
        }
        const files = await files_from_data_transfer(
            (e as any).dataTransfer || (e as any).clipboardData,
        )
        if (files.length === 0) {
            return
        }
        if (ui_state.card_editor || ui_state.dialog !== "none" || ui_state.lightbox_shown()) {
            Snackbar.show_message("Close all editors and dialogs to add files.")
            return
        }
        if (!board) {
            return
        }
        const x = (e as any).clientX ?? client_x
        const y = (e as any).clientY ?? client_y
        // We use `y - 20` because users might try to drop / paste just in between two cards ...
        let card = card_at(x, y - 20, board)
        if (!card) {
            card = board.root
        }
        if (!card) {
            return
        }
        e.preventDefault()
        e.stopPropagation()
        await new AddCardActions(board, () => {
            if (card!.is_regular_card) {
                return below_card_insert_pos(card!, {
                    has_visible_children: ui_state.search_state.has_visible_children(card!),
                })
            }
            return {
                target_pos: card!.inbox ? 0 : 4242424242,
                target_card_uid: card!.uid,
            }
        }).add_files(files.map((file) => ({file})))
    } catch (error) {
        report_error("Failed to paste / drop files", error)
    }
}

function card_at(x: number, y: number, board: Board) {
    // We need to hide the backdrop in order to find the element ...
    const backdrop = document.querySelector(".global-drop-and-paste") as HTMLElement
    if (backdrop) {
        backdrop.style.display = "none"
    }
    let card: Card | undefined
    const card_elm = document.elementFromPoint(x, y)?.closest("[data-card-uid]")
    if (card_elm) {
        const card_uid = card_elm.getAttribute("data-card-uid") as CardUID
        if (board.contains(card_uid)) {
            card = board.card(card_uid)
        }
    }
    return card
}
